import React, { ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { Alert, AlertColor, Box, Link, Snackbar } from '@mui/material';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useNavigate } from 'react-router-dom';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';

import { useTranslation } from 'react-i18next';
import { GridCellParams } from '@mui/x-data-grid';
import Toolbar from '../Toolbar';
import DataTable from '../DataTable';
import * as providerSlice from '../../../data/provider.slice';
import * as vehicleSlice from '../../../data/vehicle.slice';
import * as referenceSlice from '../../../data/reference.slice';
import { SortModel } from '../../../data/models/sort-model.model';
import VehicleDialog from './VehicleDialog';
import ConfirmationDialog from '../../ConfirmationDialog';
import tableUtils from '../../../services/tableUtils';
import vehicleUtils from '../../../services/vehicleUtils';
import { useCanDeleteVehicle } from '../../../hooks/permissions/useCanDeleteVehicle';
import { useCanCreateVehicle } from '../../../hooks/permissions/useCanCreateVehicle';
import { useCanEditVehicle } from '../../../hooks/permissions/useCanEditVehicle';
import { Provider } from '../../../data/models/provider.model';
import providerService from '../../../services/providerService';
import vehicleService from '../../../services/vehicleService';
import { Vehicle } from '../../../data/models/vehicle.model';
import type { BaseProvider } from '../../../data/models/provider.model';

const PREFIX = 'Vehicles';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2),
    gap: theme.spacing(2),
  },
}));

interface VehiclesProps {
  provider?: Provider | null;
  canCreate?: boolean | false;
  isOnboarding?: boolean;
}

export default function Vehicles({
  provider,
  canCreate,
  isOnboarding = false,
}: VehiclesProps) {
  const navigate = useNavigate();

  const { t } = useTranslation('common');
  const canDeleteVehicle = useCanDeleteVehicle();
  const canCreateVehicle = useCanCreateVehicle();
  const canEditVehicle = useCanEditVehicle();

  const selectedFilter = useAppSelector(vehicleSlice.selectSelectedFilter);
  const searchFilter = useAppSelector(vehicleSlice.selectSearchFilter);
  const providerVehicleSearchFilter = useAppSelector(
    providerSlice.selectVehicleSearchFilter,
  );
  const page = useAppSelector(vehicleSlice.selectPage);
  const pageSize = useAppSelector(vehicleSlice.selectPageSize);
  const sortModel = useAppSelector(vehicleSlice.selectSortModel);
  const loading = useAppSelector(vehicleSlice.selectLoadingVehicleList);
  const vehicleList = useAppSelector(vehicleSlice.selectVehicleList);
  const vehicle = useAppSelector(vehicleSlice.selectVehicle);
  const vehicleTypes = useAppSelector(referenceSlice.selectVehicleTypes);
  const importingVehicle = useAppSelector(vehicleSlice.selectImportingVehicles);
  const importVehiclesError = useAppSelector(
    vehicleSlice.selectImportVehiclesError,
  );
  const importVehiclesSuccess = useAppSelector(
    vehicleSlice.selectImportVehiclesSuccess,
  );
  const dispatch = useAppDispatch();

  const [editorOpen, setEditorOpen] = React.useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = React.useState<
    BaseProvider | undefined
  >(undefined);
  const [selectEditVehicle, setSelectEditVehicle] =
    React.useState<Vehicle | null>(null);
  const [openEditorAfterFetch, setOpenEditorAfterFetch] =
    React.useState<boolean>(false);
  const fetchedProvider = useAppSelector(providerSlice.selectProvider);

  React.useEffect(() => {
    if (provider) {
      setSelectedProvider(provider);
    }
  }, [provider]);

  React.useEffect(() => {
    dispatch(vehicleSlice.setVehicle(null));
    setOpenEditorAfterFetch(false);
  }, [dispatch]);

  React.useEffect(() => {
    const filter = '';
    const search = provider ? providerVehicleSearchFilter : searchFilter;
    dispatch(
      vehicleSlice.fetchVehicleListAsync(
        pageSize,
        page,
        filter,
        search,
        sortModel,
        provider?.transportationProviderID,
      ),
    );
  }, [
    provider,
    selectedFilter,
    searchFilter,
    providerVehicleSearchFilter,
    page,
    pageSize,
    sortModel,
    dispatch,
  ]);

  React.useEffect(() => {
    if (importVehiclesError) {
      setSnackbarMessage(importVehiclesError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [importVehiclesError]);

  const reloadVehicles = React.useCallback(() => {
    if (providerVehicleSearchFilter !== '' || sortModel !== null) {
      dispatch(providerSlice.setVehicleSearchFilter(''));
      dispatch(vehicleSlice.setSortModel(null));
    } else {
      dispatch(
        vehicleSlice.fetchVehicleListAsync(
          pageSize,
          page,
          '',
          providerVehicleSearchFilter,
          sortModel,
          provider?.transportationProviderID,
        ),
      );
    }
  }, [
    dispatch,
    page,
    pageSize,
    provider?.transportationProviderID,
    providerVehicleSearchFilter,
    sortModel,
  ]);

  React.useEffect(() => {
    if (importVehiclesSuccess) {
      setSnackbarMessage(importVehiclesSuccess);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      reloadVehicles();
    }
  }, [importVehiclesSuccess, reloadVehicles, dispatch]);

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState<string | null>('');
  const [deleteId, setDeleteId] = React.useState(0);

  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<
    AlertColor | undefined
  >();
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const renderVehicleMake = (params: GridCellParams): ReactElement => {
    return (
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          navigate('/vehicles/' + params.row.vehicleID);
        }}
        color="textPrimary"
        underline="always">
        {params.row.vehicleMake}
      </Link>
    );
  };

  const renderVehicleType = (params: GridCellParams): ReactElement => {
    return (
      <>
        {vehicleUtils.getVehicleTypeName(
          vehicleTypes,
          params.row.vehicleTypeID,
        )}
      </>
    );
  };

  const deleteRow = (
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    params: GridCellParams,
  ) => {
    deleteVehicle(
      evt,
      params.row.vehicleID,
      params.row.vehicleMake,
      params.row.vehicleModel,
    );
  };

  const deleteVehicle = (evt: any, id: number, make: string, model: string) => {
    evt.preventDefault();
    setDeleteId(id);
    setConfirmMessage(t('vehicles.confirmDelete', { make, model }));
    setConfirmOpen(true);
  };

  const confirmDeleteVehicle = () => {
    setConfirmOpen(false);
    dispatch(
      vehicleSlice.deleteVehicleAsync(
        provider?.transportationProviderID,
        deleteId,
      ),
    );
  };

  const cancelDeleteVehicle = () => {
    setConfirmOpen(false);
  };

  const onVehicleEdit = (row: any) => {
    if (!provider && row?.transportationProviderID) {
      dispatch(
        providerSlice.getProviderAsync(row.transportationProviderID) as any,
      );
    }

    setOpenEditorAfterFetch(true);
    dispatch(vehicleSlice.getVehicleAsync(row.vehicleID) as any);
  };

  React.useEffect(() => {
    if (!provider && fetchedProvider) {
      setSelectedProvider(fetchedProvider);
    }
  }, [provider, fetchedProvider]);

  React.useEffect(() => {
    if (openEditorAfterFetch && vehicle) {
      setSelectEditVehicle(vehicle);
      setEditorOpen(true);
      setOpenEditorAfterFetch(false);
    }
  }, [vehicle, openEditorAfterFetch]);

  const columns = tableUtils.getVehicleColumnDefinition(
    renderVehicleMake,
    renderVehicleType,
    t,
    canDeleteVehicle ? deleteRow : null,
    canEditVehicle ? onVehicleEdit : null,
  );

  const onSearchChange = (val: string) => {
    if (provider) {
      dispatch(providerSlice.setVehicleSearchFilter(val));
    } else {
      dispatch(vehicleSlice.setSearchFilter(val));
    }
  };

  const onSelectedFilterChange = (val: string) => {
    dispatch(vehicleSlice.setSelectedFilter(val));
  };

  const onPageChange = (pg: number) => {
    dispatch(vehicleSlice.setPage(pg));
  };

  const onPageSizeChange = (pgSize: number) => {
    dispatch(vehicleSlice.setPageSize(pgSize));
  };

  const onSortModelChange = (model: SortModel | null) => {
    dispatch(vehicleSlice.setSortModel(model));
  };

  const openVehicleEditor = () => {
    setEditorOpen(true);
    setSelectEditVehicle(null);
  };

  const closeVehicleEditor = (saved: boolean) => {
    if (editorOpen && saved && vehicle && !isOnboarding) {
      navigate('/vehicles/' + vehicle.vehicleID);
    }

    if (isOnboarding) {
      dispatch(vehicleSlice.resetSavingFlags());
    }

    setEditorOpen(false);
  };

  const importVehicles = (file: File) => {
    if (provider?.transportationProviderID) {
      dispatch(
        vehicleSlice.importVehiclesAsync(
          provider.transportationProviderID,
          file,
        ),
      );
    }
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
    dispatch(vehicleSlice.resetImportFlags());
  };

  return (
    <StyledBox className={classes.container}>
      <Toolbar
        button={
          provider && (canCreate || canCreateVehicle)
            ? t('vehicles.add')
            : undefined
        }
        importButton={
          provider && (canCreate || canCreateVehicle)
            ? t('vehicles.import')
            : undefined
        }
        disableImport={importingVehicle}
        filterOptions={undefined}
        currentFilter={selectedFilter}
        searchIcon={<AirportShuttleIcon />}
        searchPlaceholder={t('vehicles.search')}
        currentSearchString={
          provider ? providerVehicleSearchFilter : searchFilter
        }
        onFilterChange={onSelectedFilterChange}
        onSearchChange={onSearchChange}
        onCreate={openVehicleEditor}
        onImport={importVehicles}
      />
      <DataTable
        getRowId={(row) => row.vehicleID}
        columns={columns}
        itemList={vehicleList}
        pageSize={pageSize}
        sortModel={sortModel}
        loading={loading}
        rowSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={onSortModelChange}
      />
      {provider || selectedProvider ? (
        <VehicleDialog
          title={selectEditVehicle ? t('vehicles.edit') : t('vehicles.create')}
          provider={(selectedProvider || provider)!}
          vehicle={selectEditVehicle || undefined}
          open={editorOpen}
          close={closeVehicleEditor}
        />
      ) : null}
      <ConfirmationDialog
        title={t('common.confirmDelete')}
        message={confirmMessage || ''}
        open={confirmOpen}
        positiveAction={confirmDeleteVehicle}
        negativeAction={cancelDeleteVehicle}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => closeSnackbar()}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </StyledBox>
  );
}
